/**
 * La plantilla de «Qué les decís»: elegirla, guardarla y la plantilla en el momento (SPEC-BD C1-C2).
 *
 * - El borrador ya existe: nace en «A quién» (29/10/2026). Elegir una plantilla aprobada o en revisión la guarda
 *   enseguida, así sus variables se completan debajo, en la misma pantalla. Otra plantilla borra las variables elegidas
 *   (`mapeo: {}`): si la campaña ya tenía, se pregunta en línea antes (`preguntarCambio`), y lo calculado con la
 *   anterior se descarta (`alCambiarPlantilla`).
 * - Crear una plantilla, partir de un modelo o duplicar una se hace en una PÁGINA aparte (`campanas/:id/plantilla?editor=`,
 *   ver `logic/editorCampana.ts`), nunca en una ventana encima del asistente: se trabaja más cómodo y el «atrás» del
 *   navegador vuelve al editor. Al volver, la página deja su aviso en `location.state`. Ir allá desmonta el asistente: lo
 *   elegido sin guardar (la plantilla, la prueba) viaja en sessionStorage el tiempo del viaje (`FotoAsistente`).
 *   `?publico=` va y vuelve por la URL.
 * - Con la plantilla en revisión se completan las variables y «Cuándo»; «Revisar y mandar» espera la aprobación
 *   (`maxPasoAlcanzable`). Nada se manda solo cuando Meta la aprueba (`alCambiarEstado`).
 * - Otra plantilla elegida y sin guardar (la pregunta sin contestar, un guardado que falló, la vuelta del editor con
 *   `?plantilla=`) es un cambio sin guardar (`cambios`): saltar a otro paso pregunta antes. Los pasos de después usan la
 *   guardada.
 */

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, type SetURLSearchParams } from "react-router-dom";
import type { CampanaDetalle, ConfigCuenta, ModoTransporte, PlantillaDetalle, PlantillaResumen } from "../../types";
import { oficialApi } from "../../api";
import { ESTADOS_PLANTILLA_PARA_SEGUIR, TEXTO_PLANTILLA_ARCHIVADA, maxPasoAlcanzable, type PasoAsistente } from "../../logic/chequeo";
import { variablesSinClics } from "../../logic/clics";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import type { Lectura } from "../../logic/respuesta";
import { motivoPlantillaNoUsable, textoRechazoPlantilla } from "../../logic/textos";
import { leerEditor, rutaPlantillaDeCampana } from "../../logic/editorCampana";
import { esInutilizable, guardarFoto, hastaDonde, idx, type Aviso, type CambiosDelPaso, type CtaPaso, type FotoAsistente } from "../../logic/asistente";
import type { BorradorCampana } from "./useBorradorCampana";
import type { PruebaCampana } from "./usePruebaCampana";

/**
 * Texto de un rechazo al asociar una plantilla a la campaña. Cada código dice lo suyo (A1-4, R-E3):
 * PLANTILLA_SIMULADA y PLANTILLA_DE_OTRA_CUENTA explican por qué una plantilla que en la lista
 * figura aprobada no sirve; PLANTILLA_NO_APROBADA es sólo «en revisión o rechazada». El resto
 * (PLANTILLA_NO_ASOCIABLE: pausada, borrador, archivada…) trae su texto del backend.
 */
export function avisoRechazoPlantilla(l: Extract<Lectura<unknown>, { ok: false }>): Aviso {
  return { texto: textoRechazoPlantilla(l.code) || l.error || textoError(l) };
}
/** Códigos con los que el backend no deja asociar una plantilla (SPEC-BD B-10, A1-4, A1-6): la lista se vuelve a pedir. */
export const RECHAZOS_PLANTILLA = new Set(["PLANTILLA_NO_APROBADA", "PLANTILLA_NO_ASOCIABLE", "PLANTILLA_SIMULADA", "PLANTILLA_DE_OTRA_CUENTA"]);

export function usePlantillaCampana({
  borrador, config, modo, idRuta, sp, setSp, publicoUrl, prueba,
  setMaxAlcanzado, irA, setErrorPaso, setAvisoOk, antesDeCambiarPlantilla, antesDeIrAlEditor, alCambiarPlantilla, descartarChequeo,
}: {
  borrador: BorradorCampana;
  config: ConfigCuenta | null;
  modo: ModoTransporte | null;
  idRuta: string | undefined;
  sp: URLSearchParams;
  setSp: SetURLSearchParams;
  publicoUrl: string;
  /** lo que viaja al editor y vuelve (`FotoAsistente`) */
  prueba: Pick<PruebaCampana, "vista" | "sinPrueba" | "inicio">;
  setMaxAlcanzado: Dispatch<SetStateAction<number>>;
  irA: (p: PasoAsistente) => void;
  setErrorPaso: (a: Aviso | null) => void;
  setAvisoOk: (s: string) => void;
  /** se va a guardar otra plantilla: lo que estaba por guardarse de las variables era de la anterior */
  antesDeCambiarPlantilla: () => void;
  /** se va al editor: lo que estaba por guardarse de las variables es de ésta, y se guarda antes de irse */
  antesDeIrAlEditor: () => Promise<unknown>;
  /** otra plantilla: el mapeo, la previa, la prueba y el chequeo eran de la anterior */
  alCambiarPlantilla: () => void;
  /** Meta aprobó la plantilla: el costo depende de la categoría final y el chequeo se recalcula */
  descartarChequeo: () => void;
}) {
  const navigate = useNavigate();
  const {
    campana, setCampana, campanaRef, registrarGuardado, guardando, setGuardando, plantillaId, setPlantillaId,
    setPlantillas, cargarPlantillas, detalles, setDetalles, camposValidos, estadoElegida, elegidaInutilizable, elegidaArchivada,
  } = borrador;
  // Cambiar la plantilla de una campaña con variables elegidas las borra: se pregunta en línea (antes, `window.confirm`).
  const [preguntarCambio, setPreguntarCambio] = useState(false);

  // ── Plantilla en el momento (C1, C2) ─────────────────────────────────────
  const [avisoRechazada, setAvisoRechazada] = useState("");
  const [duplicandoId, setDuplicandoId] = useState("");
  /** `?editor=` de un link viejo o de un F5 de cuando el editor se abría encima del asistente (ver el efecto de compatibilidad). */
  const editorViejo = sp.get("editor");

  /**
   * Compatibilidad con `?editor=` (links viejos, un F5 de cuando el editor era una ventana encima del
   * asistente). Con la campaña cargada se va a la página del editor reemplazando esta entrada, así el
   * «atrás» no rebota acá. Sin campaña (`campanas/nueva`), o con un valor que no se entiende, sólo se
   * saca el parámetro: abrir el editor ahí crearía un borrador que nadie pidió.
   */
  useEffect(() => {
    if (editorViejo === null) return;
    if (idRuta && !campana) return;
    if (campana && leerEditor(editorViejo)) { navigate(rutaPlantillaDeCampana(campana.id, editorViejo, publicoUrl || undefined), { replace: true }); return; }
    setSp((prev) => { const n = new URLSearchParams(prev); n.delete("editor"); return n; }, { replace: true });
  }, [editorViejo, idRuta, campana, navigate, setSp, publicoUrl]);

  /**
   * Guarda `pid` como la plantilla de la campaña, con las variables borradas (eran de la anterior). Con variables ya
   * elegidas pregunta antes; `confirmado` es el «Sí». La misma plantilla no guarda nada. Con `destino` («Guardar y
   * seguir» de un salto), va ahí si lo guardado deja: con otra plantilla, como mucho «Qué les decís».
   */
  const guardarPlantilla = async (pid: string, confirmado = false, destino?: PasoAsistente) => {
    const c = campanaRef.current;
    if (!config || !c || !pid) return;
    if (c.plantilla?.id === pid) { setPreguntarCambio(false); return; }
    if (Object.keys(c.mapeo || {}).length > 0 && !confirmado) { setPreguntarCambio(true); return; }
    setPreguntarCambio(false);
    setGuardando(true);
    setErrorPaso(null);
    antesDeCambiarPlantilla();
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(c.id, { plantillaId: pid, mapeo: {} }));
      if (!l.ok) {
        setErrorPaso(avisoRechazoPlantilla(l));
        if (RECHAZOS_PLANTILLA.has(l.code)) void cargarPlantillas();
        return;
      }
      // Otra plantilla: el mapeo, la previa y la prueba eran de la anterior, y lo que sigue se vuelve a mirar.
      const n = Math.min(registrarGuardado(l.data), idx("mensaje"));
      alCambiarPlantilla();
      setMaxAlcanzado(n);
      if (destino) irA(hastaDonde(destino, n));
    } catch (e) {
      setErrorPaso({ texto: textoError(leerError(e)) });
    } finally {
      setGuardando(false);
    }
  };
  /** «No» a la pregunta: vuelve a la plantilla que la campaña tiene guardada. */
  const noCambiar = () => { setPreguntarCambio(false); setPlantillaId(campana?.plantilla?.id || ""); };

  const elegirPlantilla = (p: PlantillaResumen) => {
    // En modo Twilio una plantilla simulada o de otra cuenta no se puede elegir (A1-4, A1-6).
    if (motivoPlantillaNoUsable(p, modo)) return;
    setPlantillaId(p.id);
    setPreguntarCambio(false);
    // Aprobada o en revisión se guarda ya (sus variables van debajo). Una que no se pudo mandar a Meta queda elegida
    // para reintentar el envío desde su estado en vivo.
    if (ESTADOS_PLANTILLA_PARA_SEGUIR.includes(p.estado) && !p.archivada) void guardarPlantilla(p.id);
  };

  /** Lo elegido y no guardado, para encontrarlo al volver del editor (ver `FotoAsistente`). */
  const fotoParaElViaje = (asociada: string): FotoAsistente => ({
    plantillaId, asociada, sinPrueba: prueba.sinPrueba,
    prueba: prueba.vista ? { ...prueba.vista, texto: "" } : null,
    pruebaInicio: prueba.inicio,
  });

  /**
   * Va a la página del editor (`valor` es lo de `?editor=`: `nueva`, `modelos`, `modelo:<clave>`, `plantilla:<id>`).
   * `?publico=` va también a la página del editor, que lo devuelve al volver (R-B12). Antes, lo último de las variables
   * (si esperaba su debounce) se guarda: ir allá desmonta el asistente, y el editor puede asociar otra plantilla.
   */
  const abrirEditor = async (valor: string) => {
    const c = campanaRef.current;
    if (!config || !c) return;
    setErrorPaso(null);
    await antesDeIrAlEditor();
    guardarFoto(c.id, fotoParaElViaje(c.plantilla?.id || ""));
    navigate(rutaPlantillaDeCampana(c.id, valor, publicoUrl || undefined));
  };

  /**
   * Deja la copia de «Duplicar y editar» como la plantilla de la campaña, antes de ir al
   * editor (lo que se crea o se manda desde el editor lo asocia su página). Si reemplaza a otra, el mapeo,
   * la previa, la prueba y el chequeo eran de la anterior: el backend conserva las variables POR NÚMERO, y
   * el {{1}} = «nombre» de la vieja quedaría aplicado al {{1}} de la nueva, que puede ser otra cosa (R-B9).
   */
  const asociarPlantilla = async (pid: string): Promise<boolean> => {
    const c = campanaRef.current;
    if (!c) return false;
    const anterior = c.plantilla?.id || "";
    if (anterior === pid) return true;
    const conMapeo = !!anterior && Object.keys(c.mapeo || {}).length > 0;
    antesDeCambiarPlantilla();
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(c.id, { plantillaId: pid, ...(conMapeo ? { mapeo: {} } : {}) }));
      if (!l.ok) { setErrorPaso(avisoRechazoPlantilla(l)); return false; }
      const n = registrarGuardado(l.data, false);
      setPlantillaId(pid);
      if (anterior) {
        alCambiarPlantilla();
        setMaxAlcanzado(Math.min(n, idx("mensaje")));
      }
      return true;
    } catch (e) {
      setErrorPaso({ texto: textoError(leerError(e)) });
      return false;
    }
  };

  /**
   * «Duplicar y editar»: la copia es un borrador nuevo que se abre en el editor. Si no se
   * pudo asociar a la campaña, se queda acá con el error: al irse al editor, el asistente se desmonta y
   * el error se perdería con él.
   */
  const duplicarYEditar = async (pid: string) => {
    const c = campanaRef.current;
    if (duplicandoId || !c) return;
    setDuplicandoId(pid);
    setErrorPaso(null);
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.duplicarPlantilla(pid));
      if (!l.ok || !l.data?.id) { setErrorPaso({ texto: l.ok ? "El servidor no devolvió la copia." : l.error || textoError(l) }); return; }
      const copia = l.data.id;
      setDetalles((d) => ({ ...d, [copia]: l.data }));
      void cargarPlantillas();
      // Lo elegido y la prueba no hace falta guardarlos: la copia los reemplaza (`asociarPlantilla`).
      if (await asociarPlantilla(copia)) navigate(rutaPlantillaDeCampana(c.id, `plantilla:${copia}`, publicoUrl || undefined));
    } catch (e) {
      setErrorPaso({ texto: textoError(leerError(e)) });
    } finally {
      setDuplicandoId("");
    }
  };

  /**
   * El estado en vivo vio un cambio de la plantilla. Aprobada: se habilitan la prueba y el confirmar,
   * y el chequeo de «Revisar y mandar» se recalcula (el costo depende de la categoría final). Rechazada: vuelve
   * a «Qué les decís» con «Duplicar y editar». Nada sale solo (C2).
   */
  const alCambiarEstado = (p: PlantillaDetalle) => {
    setDetalles((d) => ({ ...d, [p.id]: { ...(d[p.id] || {}), ...p } }));
    setPlantillas((ps) => ps ? ps.map((x) => x.id === p.id ? { ...x, estado: p.estado, rejectionReason: p.rejectionReason } : x) : ps);
    let c = campanaRef.current;
    if (c?.plantilla?.id === p.id) {
      c = { ...c, plantilla: { ...c.plantilla, estado: p.estado, rejectionReason: p.rejectionReason } };
      campanaRef.current = c;
      setCampana(c);
    }
    const nums = variablesSinClics(p.variables || detalles[p.id]?.variables, p.botones || detalles[p.id]?.botones).map((v) => v.numero);
    if (p.estado === "approved") {
      setAvisoRechazada("");
      setAvisoOk("Meta aprobó la plantilla: ya podés mandar la prueba y confirmar. El costo y el chequeo se recalculan.");
      descartarChequeo();
      if (c) {
        const max = idx(maxPasoAlcanzable(c, nums, { plantillaInutilizable: esInutilizable(p, modo), plantillaArchivada: !!(p.archivada ?? detalles[p.id]?.archivada), camposValidos }));
        setMaxAlcanzado((m) => Math.max(m, max));
      }
    } else if (p.estado === "rejected") {
      setAvisoOk("");
      setAvisoRechazada(`Meta rechazó la plantilla: ${p.rejectionReason?.trim() || "no dio el motivo"}.`);
      if (c) setMaxAlcanzado(idx(maxPasoAlcanzable(c, nums, { camposValidos })));
    }
  };

  /** La plantilla elegida es la que la campaña tiene guardada: sus variables se completan debajo. */
  const guardada = !!plantillaId && campana?.plantilla?.id === plantillaId;
  // Se sigue con una plantilla aprobada o en revisión (B-10): la prueba y el confirmar esperan la aprobación.
  const noSirve = !plantillaId ? "Elegí una plantilla"
    : elegidaInutilizable ? "Esa plantilla es de otra cuenta y no sirve con tu número: «Duplicar y editar»"
    : elegidaArchivada ? TEXTO_PLANTILLA_ARCHIVADA
    : estadoElegida === "rejected" ? "Meta rechazó esta plantilla: corregila"
    : estadoElegida === "unsubmitted" ? "No se pudo mandar a Meta: reintentá el envío"
    : estadoElegida === "borrador" ? "Esa plantilla todavía no se mandó a Meta"
    : !estadoElegida ? "Cargando la plantilla…"
    : !ESTADOS_PLANTILLA_PARA_SEGUIR.includes(estadoElegida) ? "Esa plantilla no se puede usar" : "";
  const motivo = noSirve || (preguntarCambio ? "Contestá si cambiás la plantilla" : "");
  /** Sin guardar (no se pudo, o volvió del editor sin asociarse): el botón la guarda. Guardada, manda el de las variables. */
  const cta: CtaPaso = { motivo: motivo || null, onSiguiente: () => guardarPlantilla(plantillaId), label: guardada ? undefined : "Usar esta plantilla", guardando };
  /**
   * Otra plantilla elegida que la campaña no tiene guardada. «Guardar y seguir» con la pregunta a la vista es su «Sí»; sin
   * ella, y con variables elegidas, la hace (las borra). «Descartar» vuelve a la guardada.
   */
  const cambios: CambiosDelPaso = {
    sinGuardar: !!campana && !!plantillaId && !guardada,
    noSeGuarda: noSirve || null,
    guardar: (destino) => guardarPlantilla(plantillaId, preguntarCambio, destino),
    descartar: (destino) => { noCambiar(); irA(destino); },
  };

  return {
    cta, cambios, guardada, elegirPlantilla, guardarPlantilla, preguntarCambio, noCambiar, avisoRechazada,
    abrirEditor, duplicarYEditar, duplicandoId, alCambiarEstado,
  };
}
